import React from "react";
import { FormFieldDto } from "../../Models/FormFieldDto";
import { FieldType } from "../../Models/FieldTypes";
import ImageUploader from "../Common/ImageUploader";
import BoothMapEditor from "./BoothMapEditor";
import { QBReferenceItemDto } from "../../Models/QBReferenceItemDto";

interface FieldEditorProps {
  field: FormFieldDto;
  index: number;
  onChange: (index: number, changes: Partial<FormFieldDto>) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: number) => void;
  qbItems: QBReferenceItemDto[]; // <- ADD THIS
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  index,
  onChange,
  onRemove,
  onMove,
  qbItems,
}) => {
  const toFiniteNumber = (value: string | number, fallback = 0): number => {
    const parsed = typeof value === "number" ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const toFiniteIntOrNull = (value: string | number): number | null => {
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleChange = (prop: keyof FormFieldDto, value: any) => {
    onChange(index, { [prop]: value });
  };

  const updateOption = (
    optIndex: number,
    prop: "name" | "value" | "amount" | "qbRefId" | "hasUsers" | "userAddType" | "userAddCount" | "userTypes",
    value: string | boolean | number | any[]
  ) => {
    const options = [...(field.options || [])];
    const updated = {
      ...options[optIndex],
      [prop]:
        prop === "amount"
          ? toFiniteNumber(value as string, 0)
          : prop === "qbRefId" || prop === "userAddCount"
          ? toFiniteIntOrNull(value as string)
          : value,
    };
    options[optIndex] = updated;
    handleChange("options", options);
  };

  const addOption = () => {
    const options = [
      ...(field.options || []),
      { name: "", value: "", amount: 0 },
    ];
    handleChange("options", options);
  };

  const removeOption = (optIndex: number) => {
    const options = field.options?.filter((_, i) => i !== optIndex) || [];
    handleChange("options", options);
  };

  return (
    <div className="p-4 mb-2 border rounded bg-white shadow">
      <div className="grid grid-cols-1 gap-4">
        <select
          className="w-full p-2 border rounded"
          value={field.fieldType}
          onChange={(e) =>
            handleChange("fieldType", e.target.value as FieldType)
          }
        >
          <option>SingleText</option>
          <option>MultiLineText</option>
          <option>DateRange</option>
          <option>Date</option>
          <option>Time</option>
          <option>Combo</option>
          <option>Radio</option>
          <option>Checkbox</option>
          <option>Section Title</option>
          <option>Section Divider</option>
          <option>Image Section</option>
          <option>BoothPicker</option>
        </select>

        {field.fieldType !== "Section Divider" &&
        field.fieldType !== "Image Section" &&
        field.fieldType !== "BoothPicker" ? (
          <>
            <input
              className="w-full p-2 border rounded"
              placeholder="Label"
              value={field.label}
              onChange={(e) => handleChange("label", e.target.value)}
            />

            <textarea
              className="w-full p-2 border rounded"
              placeholder="Description"
              value={field.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
            />
          </>
        ) : (
          ""
        )}
        {field.fieldType == "Image Section" ? (
          <>
            <input
              className="w-full p-2 border rounded"
              placeholder="Label"
              value={field.label}
              onChange={(e) => handleChange("label", e.target.value)}
            />
            <ImageUploader
              onUploaded={(url) => onChange(index, { defaultValue: url })}
            />
            {field.defaultValue && (
              <img
                src={field.defaultValue}
                alt="Preview"
                className="max-w-xs mt-2"
              />
            )}
          </>
        ) : field.fieldType === "BoothPicker" ? (
          <>
            <input
              className="w-full p-2 border rounded"
              placeholder="Label (include &quot;Booth&quot; for event roster)"
              value={field.label}
              onChange={(e) => handleChange("label", e.target.value)}
            />
            <textarea
              className="w-full p-2 border rounded"
              placeholder="Description / instructions"
              value={field.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
            />
            <BoothMapEditor
              field={field}
              onChange={(changes) => onChange(index, changes)}
            />
          </>
        ) : (
          ""
        )}

        {field.fieldType === "Date" ? (
          <input
            type="date"
            className="p-2 border rounded"
            value={field.defaultValue || ""}
            onChange={(e) => handleChange("defaultValue", e.target.value)}
          />
        ) : field.fieldType === "Time" ? (
          <input
            type="time"
            className="p-2 border rounded"
            value={field.defaultValue || ""}
            onChange={(e) => handleChange("defaultValue", e.target.value)}
          />
        ) : (
          " "
        )}
        {field.fieldType !== "Section Title" &&
          field.fieldType !== "Section Divider" &&
          field.fieldType !== "BoothPicker" && (
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={field.isRequired}
                onChange={(e) => handleChange("isRequired", e.target.checked)}
              />
              <span className="ml-2">Required</span>
            </label>
          )}

        {field.fieldType === "BoothPicker" && (
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={field.isRequired}
              onChange={(e) => handleChange("isRequired", e.target.checked)}
            />
            <span className="ml-2">Required</span>
          </label>
        )}

        {(field.fieldType === "Combo" ||
          field.fieldType === "Radio" ||
          field.fieldType === "Checkbox") && (
          <div className="">
            <label className="block text-sm font-medium mb-1">Options</label>
            {field.options?.map((opt, i) => (
              <div key={i} className="mb-4">
                <div className="flex flex-wrap items-end gap-2">
                  {/* Option Name */}
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Option Name
                    </label>
                    <input
                      className="p-1 border rounded w-full"
                      placeholder="e.g. Basic Package"
                      value={opt.name}
                      onChange={(e) => updateOption(i, "name", e.target.value)}
                    />
                  </div>

                  {/* Amount */}
                  <div className="w-32">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount
                    </label>
                    <input
                      className="p-1 border rounded w-full"
                      type="number"
                      step="0.01"
                      placeholder="e.g. 19.99"
                      value={Number.isFinite(opt.amount) ? opt.amount : 0}
                      onChange={(e) =>
                        updateOption(i, "amount", e.target.value)
                      }
                    />
                  </div>

                  {/* QuickBooks Item */}
                  {opt.amount && opt.amount > 0 && (
                    <div className="w-64">
                      <label className="block font-medium mb-1">
                        QuickBooks Item
                      </label>
                      <select
                        className="w-full p-2 border rounded"
                        value={opt.qbRefId || ""}
                        onChange={(e) =>
                          updateOption(i, "qbRefId", e.target.value)
                        }
                      >
                        <option value="">-- Select Item --</option>
                        {qbItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.qbitemName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Remove Button */}
                  <div className="flex items-center h-full">
                    <button
                      type="button"
                      className="text-sm text-red-600"
                      onClick={() => removeOption(i)}
                    >
                      ✕ Remove Option
                    </button>
                  </div>
                </div>

                {/* User-based pricing for this option */}
                <div className="ml-4 mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={opt.hasUsers || false}
                      onChange={(e) =>
                        updateOption(i, "hasUsers", e.target.checked)
                      }
                    />
                    <span className="ml-2 text-sm font-medium">
                      Requires Additional User Information
                    </span>
                  </label>

                  {opt.hasUsers && (
                    <div className="mt-2 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            User Input Type
                          </label>
                          <select
                            className="p-2 border rounded w-full text-sm"
                            value={opt.userAddType || "Fixed"}
                            onChange={(e) =>
                              updateOption(i, "userAddType", e.target.value)
                            }
                          >
                            <option value="Fixed">Fixed Count</option>
                            <option value="Variable">User Selects Count</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {opt.userAddType === "Variable"
                              ? "User chooses how many people"
                              : "Fixed number of users required"}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">
                            {opt.userAddType === "Variable"
                              ? "Max Users"
                              : "Number of Users"}
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            className="p-2 border rounded w-full text-sm"
                            placeholder="e.g. 3"
                            value={opt.userAddCount || 1}
                            onChange={(e) =>
                              updateOption(i, "userAddCount", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="border-t pt-2">
                        <label className="block text-xs font-medium mb-2">
                          User Types & Pricing
                        </label>
                        {(opt.userTypes || []).map((userType, typeIdx) => (
                          <div key={typeIdx} className="flex gap-2 mb-2">
                            <input
                              type="text"
                              className="flex-1 p-1 border rounded text-sm"
                              placeholder="Type (e.g., Junior)"
                              value={userType.typeName}
                              onChange={(e) => {
                                const types = [...(opt.userTypes || [])];
                                types[typeIdx] = { ...types[typeIdx], typeName: e.target.value };
                                updateOption(i, "userTypes", types);
                              }}
                            />
                            <input
                              type="number"
                              step="0.01"
                              className="w-24 p-1 border rounded text-sm"
                              placeholder="Price"
                              value={userType.price}
                              onChange={(e) => {
                                const types = [...(opt.userTypes || [])];
                                types[typeIdx] = {
                                  ...types[typeIdx],
                                  price: toFiniteNumber(e.target.value, 0),
                                };
                                updateOption(i, "userTypes", types);
                              }}
                            />
                            <select
                              className="w-40 p-1 border rounded text-sm"
                              value={userType.qbRefId || ""}
                              onChange={(e) => {
                                const types = [...(opt.userTypes || [])];
                                types[typeIdx] = {
                                  ...types[typeIdx],
                                  qbRefId: toFiniteIntOrNull(e.target.value),
                                };
                                updateOption(i, "userTypes", types);
                              }}
                            >
                              <option value="">-- QB Item --</option>
                              {qbItems.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.qbitemName}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="text-red-600 text-xs"
                              onClick={() => {
                                const types = opt.userTypes?.filter((_, ti) => ti !== typeIdx) || [];
                                updateOption(i, "userTypes", types);
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="mt-1 px-2 py-1 bg-gray-100 text-xs rounded"
                          onClick={() => {
                            const types = [...(opt.userTypes || []), { typeName: "", price: 0, qbRefId: null }];
                            updateOption(i, "userTypes", types);
                          }}
                        >
                          + Add User Type
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              className="mt-2 px-2 py-1 bg-gray-200 text-sm rounded"
              onClick={addOption}
            >
              + Add Option
            </button>
          </div>
        )}
        {/* Price field for fields like DateRange or others */}
        {field.fieldType === "DateRange" && (
          <div className="w-full ">
            <label className="block text-sm mb-1 font-medium">
              Price per day
            </label>
            <input
              type="number"
              className="p-2 border rounded w-full"
              placeholder="Enter daily price"
              value={field.price || 0}
              onChange={(e) =>
                handleChange("price", toFiniteNumber(e.target.value, 0))
              }
            />
          </div>
        )}

        {/* User-based pricing for field-level */}
        {field.fieldType !== "Section Title" &&
          field.fieldType !== "Section Divider" &&
          field.fieldType !== "Image Section" &&
          field.fieldType !== "BoothPicker" && (
            <div className="border-t pt-4 mt-4">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={field.hasUsers || false}
                  onChange={(e) => handleChange("hasUsers", e.target.checked)}
                />
                <span className="ml-2 font-medium">Requires User Information</span>
              </label>
              <p className="text-sm text-gray-600 ml-6">
                Collect additional user details (names) for this field
              </p>

              {field.hasUsers && (
                <div className="mt-3 ml-6 space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Number of Users to Collect
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="p-2 border rounded w-32"
                      placeholder="e.g. 3"
                      value={field.userAddCount || 1}
                      onChange={(e) =>
                        handleChange("userAddCount", parseInt(e.target.value) || 1)
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The form will ask for this many users' first and last names
                    </p>
                  </div>

                  <div className="border-t pt-3">
                    <label className="block text-sm font-medium mb-2">
                      User Types & Pricing
                    </label>
                    <p className="text-xs text-gray-600 mb-3">
                      Define different types of users with individual pricing (e.g., Junior, Senior, Adult)
                    </p>
                    
                    {(field.userTypes || []).map((userType, idx) => (
                      <div key={idx} className="flex gap-2 mb-2 items-start">
                        <div className="flex-1">
                          <input
                            type="text"
                            className="w-full p-2 border rounded text-sm"
                            placeholder="Type name (e.g., Adult)"
                            value={userType.typeName}
                            onChange={(e) => {
                              const types = [...(field.userTypes || [])];
                              types[idx] = { ...types[idx], typeName: e.target.value };
                              handleChange("userTypes", types);
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <input
                            type="number"
                            step="0.01"
                            className="w-full p-2 border rounded text-sm"
                            placeholder="Price"
                            value={userType.price}
                            onChange={(e) => {
                              const types = [...(field.userTypes || [])];
                              types[idx] = {
                                ...types[idx],
                                price: toFiniteNumber(e.target.value, 0),
                              };
                              handleChange("userTypes", types);
                            }}
                          />
                        </div>
                        <div className="w-48">
                          <select
                            className="w-full p-2 border rounded text-sm"
                            value={userType.qbRefId || ""}
                            onChange={(e) => {
                              const types = [...(field.userTypes || [])];
                              types[idx] = {
                                ...types[idx],
                                qbRefId: toFiniteIntOrNull(e.target.value),
                              };
                              handleChange("userTypes", types);
                            }}
                          >
                            <option value="">-- QB Item --</option>
                            {qbItems.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.qbitemName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="text-red-600 text-sm p-2"
                          onClick={() => {
                            const types = field.userTypes?.filter((_, i) => i !== idx) || [];
                            handleChange("userTypes", types);
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      className="mt-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200"
                      onClick={() => {
                        const types = [...(field.userTypes || []), { typeName: "", price: 0, qbRefId: null }];
                        handleChange("userTypes", types);
                      }}
                    >
                      + Add User Type
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
      </div>

      <div className="flex mt-2 space-x-2">
        <button
          className="text-sm text-blue-600"
          onClick={() => onMove(index, -1)}
        >
          ↑
        </button>
        <button
          className="text-sm text-blue-600"
          onClick={() => onMove(index, 1)}
        >
          ↓
        </button>
        <button
          className="text-sm text-red-600"
          onClick={() => onRemove(index)}
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default FieldEditor;
